import {
  INQUIRY_LIMIT,
  MAX_META_DESCRIPTION_LENGTH,
  BUNNY_FB,
  BUNNY_TW,
  BUNNY_AUTHOR,
  OG_IMAGE_WIDTH,
  OG_IMAGE_HEIGHT,
} from './constants';

export const calculateOffset = (
  offset: OffsetSettings | undefined | null,
  unit: CSSUnit = 'rem'
) => {
  if (!offset || !offset.horizontal) {
    return '';
  }

  return `translate(${offset.horizontal}${unit}, ${offset.vertical}${unit})`;
};

export const calculateScale = (scale: ScaleSettings | undefined | null) => {
  if (!scale || !scale.x) {
    return '';
  }

  return `scale(${scale.x}, ${scale.y})`;
};

export const getButtonStyleClass = (style?: string) => {
  switch (style) {
    case 'primary':
      return 'bunny-button--primary';
    case 'secondary':
      return 'bunny-button--secondary';
    case 'orange':
      return 'bunny-button--orange';
    case 'white':
      return 'bunny-button--white';
    default:
      return 'bunny-button--primary';
  }
};

export const getNavigatorLanguage = () =>
  navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : (navigator as any).userLanguage ||
      navigator.language ||
      (navigator as any).browserLanguage ||
      'en';

export const parseLanguageToCountryAbbreviation = () => {
  const language = getNavigatorLanguage();
  return language.split('-')[0];
};

export const getStyleTransform = (
  offset?: OffsetSettings,
  scale?: ScaleSettings
): React.CSSProperties => {
  const offsetTransform = calculateOffset(offset);
  const scaleTransform = calculateScale(scale);

  return {
    transform: `${offsetTransform} ${scaleTransform}`,
  };
};

export const getImageTransform = (
  image_field:
    | ImageField<RegularImage | ReusableImageField | ReusableRiveField>
    | null
    | undefined
) => {
  if (!image_field) {
    return {};
  }

  return getStyleTransform(image_field.offset, image_field.scale);
};

export const fixSubmenuHeight = () => {
  const submenusLvl1 = document.querySelectorAll<HTMLElement>(
    '.menu-desktop .menu-level-1'
  );
  if (!submenusLvl1?.length) return;

  for (const submenuLvl1 of Array.from(submenusLvl1)) {
    let highest = submenuLvl1.offsetHeight || 0;

    const submenusLvl2 = submenuLvl1.querySelectorAll<HTMLElement>(
      '.menu-desktop .menu-level-2'
    );
    if (submenusLvl2?.length) {
      for (const submenuLvl2 of Array.from(submenusLvl2)) {
        highest = Math.max(highest, submenuLvl2.offsetHeight || 0);

        const submenusLvl3 = submenuLvl2.querySelectorAll<HTMLElement>(
          '.menu-desktop .menu-level-3'
        );
        if (submenusLvl3?.length) {
          for (const submenuLvl3 of Array.from(submenusLvl3)) {
            highest = Math.max(highest, submenuLvl3.offsetHeight || 0);
          }
        }
      }
    }

    const styleHeight = `${highest}px`;
    submenuLvl1.style.height = styleHeight;

    const level2Items = submenuLvl1.querySelectorAll<HTMLElement>(
      '.menu-desktop .menu-level-2'
    );
    if (level2Items?.length) {
      level2Items.forEach(menuItem => {
        menuItem.style.height = styleHeight;
      });
    }

    const level3Items = submenuLvl1.querySelectorAll<HTMLElement>(
      '.menu-desktop .menu-level-3'
    );
    if (level3Items?.length) {
      level3Items.forEach(menuItem => {
        menuItem.style.height = styleHeight;
      });
    }
  }
};

export const preloadImage = (src: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = resolve;
    img.onerror = reject;
  });
};

const styleBoxElement = (attribute: string = '', prefix: string) => {
  switch (attribute) {
    case 'None':
      return ` ${prefix}-none`;
    case 'Small':
      return ` ${prefix}-small`;
    case 'Medium':
      return ` ${prefix}-medium`;
    case 'Large':
      return ` ${prefix}-large`;
    default:
      return '';
  }
};

export const clickScrollHandler = (event: React.MouseEvent<any>) => {
  event?.preventDefault();

  if (typeof window !== 'undefined') {
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  }
};

export const getPositionClass = (position?: PositionOption) => {
  if (position === 'left') {
    return 'justify-start';
  } else if (position === 'right') {
    return 'justify-end';
  }
  return 'justify-center';
};

export const scrollToElement = (event: React.MouseEvent<any>, id: string) => {
  event.preventDefault();
  if (id !== '#') {
    const target = document.querySelector(id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
};

export const SIGNUP_BUTTON_TITLE = 'Get Started';
export const LOGIN_BUTTONO_TITLE = 'Login';

export const isDashboardButton = (title: string) =>
  [SIGNUP_BUTTON_TITLE, LOGIN_BUTTONO_TITLE].includes(title);

export const appendButtonUrl = (menuUrl: string, location: Location) => {
  const { pathname } = location;
  let currentPath = menuUrl.split('&pk_buttonurl')[0];
  // @ts-ignore and remove all non alphanumeric characters
  let buttonPath = pathname ? pathname.replace(/[^0-9A-Z]+/gi, '') : '';
  // create the suffix
  return buttonPath ? `${currentPath}&pk_buttonurl=${buttonPath}` : currentPath;
};

export const getManualPositioning = (positioning: ManualPositioning) => {
  if (!positioning) {
    return {
      mobile: undefined,
      desktop: undefined,
    };
  }

  let mobile: React.CSSProperties | undefined;
  let desktop: React.CSSProperties | undefined;

  if (positioning?.mobile) {
    const { offset, scale } = positioning.mobile;
    mobile = {
      '--translate-x': `${offset?.horizontal ?? 0}rem`,
      '--translate-y': `${offset?.vertical ?? 0}rem`,
      '--scale-x': scale?.x ?? 1,
      '--scale-y': scale?.y ?? 1,
    } as React.CSSProperties;
  }

  if (positioning?.desktop) {
    const { offset, scale } = positioning.desktop;
    desktop = {
      '--translate-x': `${offset?.horizontal ?? 0}rem`,
      '--translate-y': `${offset?.vertical ?? 0}rem`,
      '--scale-x': scale?.x ?? 1,
      '--scale-y': scale?.y ?? 1,
    } as React.CSSProperties;
  }

  return {
    mobile,
    desktop,
  };
};

export const getCompleteStyleManualPositioning = (
  positioning: ManualPositioning
) => {
  if (!positioning) {
    return {
      mobile: undefined,
      desktop: undefined,
    };
  }

  let mobile: React.CSSProperties | undefined;
  let desktop: React.CSSProperties | undefined;

  if (positioning?.mobile) {
    const { offset, scale } = positioning.mobile;
    mobile = {
      translate: `${offset?.horizontal ?? 0}rem ${offset?.vertical ?? 0}rem`,
      scale: `${scale?.x ?? 1} ${scale?.y ?? 1}`,
    } as React.CSSProperties;
  }

  if (positioning?.desktop) {
    const { offset, scale } = positioning.desktop;
    desktop = {
      translate: `${offset?.horizontal ?? 0}rem ${offset?.vertical ?? 0}rem`,
      scale: `${scale?.x ?? 1} ${scale?.y ?? 1}`,
    } as React.CSSProperties;
  }

  return {
    mobile,
    desktop,
  };
};

export const getVisibility = (device?: string) => {
  if (!device || device === 'hidden') {
    return '';
  }

  return ['mobile', 'desktop', 'all'].includes(device)
    ? `is-${device}`
    : 'is-all';
};

export const getMarginBottomClass = (settings: Settings) => {
  if (!settings) return '';
  return styleBoxElement(settings.margin_bottom, 'mb');
};

export const getPaddingBottomClass = (settings: Settings) => {
  if (!settings) return '';
  return styleBoxElement(settings.padding_bottom, 'pb');
};

export const getPaddingTopClass = (settings: Settings) => {
  if (!settings) return '';
  return styleBoxElement(settings.padding_top, 'pt');
};

export const getBgColorClass = (background_color: string) => {
  if (background_color === 'Gradient - Light blue') {
    return 'bg--gradient--light-blue';
  } else if (background_color === 'Gradient - Dark blue') {
    return 'bg--gradient--dark-blue';
  } else if (background_color === 'Solid - Dark blue') {
    return 'bg--solid--dark-blue';
  } else if (background_color === 'Solid - Light blue') {
    return 'bg--solid--light-blue';
  }
  return '';
};

export const getMarginTopClass = (settings: Settings) => {
  if (!settings) return '';
  return styleBoxElement(settings.margin_top, 'mt');
};

export const getBgIllustrationClass = (background_illustration: string) => {
  if (background_illustration === 'General') {
    return 'il-general';
  } else if (background_illustration === 'Carrots') {
    return 'il-carrots';
  } else if (background_illustration === 'Christmas') {
    return 'il-christmas';
  } else if (background_illustration === 'Security') {
    return 'il-security';
  }
  return '';
};

export const getPropValues = (propValue: any, propDefault: string) =>
  typeof propValue !== 'string' || propValue === '' ? propDefault : propValue;

export const getSettingsClass = (settings?: Settings) => {
  if (!settings) return '';
  let settingsClass = settings.css_class_name ? settings.css_class_name : '';
  settingsClass += settings.custom_css ? ` ${settings.custom_css}` : '';
  settingsClass += getMarginBottomClass(settings);
  settingsClass += getMarginTopClass(settings);
  settingsClass += getPaddingBottomClass(settings);
  settingsClass += getPaddingTopClass(settings);
  return settingsClass;
};

export const nospace = (value: string) => {
  return value.replace(/\s/g, '');
};

export const getParentPageSubmenu = (parentPage?: {
  frontmatter: Frontmatter;
  url_alias: string;
}): SubmenuField | null => {
  if (!parentPage?.frontmatter?.sections) {
    return null;
  }

  const [firstSection] = parentPage.frontmatter.sections;
  if (!firstSection) {
    return null;
  }

  const [firstBlock] = firstSection.blocks;
  if (!firstBlock || !firstBlock.submenu_field?.length) {
    return null;
  }

  return firstBlock.submenu_field;
};

export const getImageFieldStyle = (
  image_field: ImageField<RegularImage>
): React.CSSProperties => {
  return {
    '--translate-x': `${image_field?.offset?.horizontal ?? 0}rem`,
    '--translate-y': `${image_field?.offset?.vertical ?? 0}rem`,
    '--scale-x': image_field?.scale?.x ?? 1,
    '--scale-y': image_field?.scale?.y ?? 1,
  } as React.CSSProperties;
};

export const classnames = (...args: string[]) => args.filter(Boolean).join(' ');

/**
 * Returns URL parameter
 * @param name - parameter name
 */
export const getQueryParam = (name: string) => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    let searchValue;

    if (urlParams.has(name)) {
      searchValue = urlParams.get(name);
    }

    return searchValue ? searchValue : '';
  }

  return '';
};

export const findHeroImage = (firstSection: { blocks: Block[] }) => {
  const heroBlock = firstSection.blocks.find(
    ({ _template }) => _template === 'block__hero'
  );
  return heroBlock?.image || heroBlock?.rive_image || null;
};

export const changeQueryParam = (name: string, value: string) => {
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    if (!value || value.length === 0) {
      url.searchParams.delete(name);
    } else {
      url.searchParams.set(name, value);
    }
    window.history.pushState({}, '', url);
  }
};

export const getCTAStyleClass = (style?: string) => {
  if (style === 'Secondary') {
    return 'cta-secondary';
  } else if (style === 'Tertiary') {
    return 'cta-tertiary';
  } else if (style === 'Tertiary - Blue') {
    return 'cta-tertiary-blue';
  }
  return 'cta-primary';
};

export const cn = (classString: string) => {
  // Replace one or more sequential whitespaces with a single space
  return classString.replace(/\s+/g, ' ').trimStart().trimEnd();
};

export const isOverInquiryLimit = (value: number) => {
  return value >= INQUIRY_LIMIT;
};

export const trimText = (text: string, length: number) => {
  if (!text) {
    return '';
  }
  // length - 3, because of 3 dots
  return text.length > length ? text.substring(0, length - 3) + '...' : text;
};

const getBlogMetaImageUrl = (
  post: {
    feature_image: string;
    published_at: string;
    updated_at: string;
    tags: { name: string }[];
  },
  siteUrl: string,
  og_image: string
) => {
  if (post.feature_image) {
    return post.feature_image;
  }

  return `${siteUrl}${og_image}`;
};

function getAcademyMetaImageUrl(
  article: {
    MetaImage: string;
    MetaTitle: string;
    MetaDesc: string;
    MetaKeywords: string;
    Category: string;
  },
  siteUrl: string,
  og_image: string
) {
  if (article.MetaImage) {
    return article.MetaImage;
  }

  return `${siteUrl}${og_image}`;
}

export const getTitleSizeStyle = (title_size: TitleSize) => {
  const titleSize = {} as TitleSizeStyle;

  if (title_size) {
    const { desktop, mobile } = title_size;
    if (desktop && desktop > 0) {
      titleSize.desktop = { fontSize: `${desktop}rem`, lineHeight: 1.2 };
    }
    if (mobile && mobile > 0) {
      titleSize.mobile = { fontSize: `${mobile}rem`, lineHeight: 1.2 };
    }
  }

  return titleSize;
};

export const getMobileImagePlacement = (
  mobile_image_placement?: MobileImagePlacement
) => {
  if (!mobile_image_placement) {
    return undefined;
  }

  const mobileImagePlacement = {
    translate: mobile_image_placement?.placement
      ? `${mobile_image_placement.placement}% 0`
      : undefined,
    maxWidth: mobile_image_placement?.max_width
      ? `${mobile_image_placement.max_width}%`
      : undefined,
  };
  return mobileImagePlacement;
};

export const getPageRichSnippets = (meta: PageMeta) => {
  if (!meta || !meta.rich_snippets) {
    return undefined;
  }
  const { rich_snippets } = meta;

  const schema = [];

  for (const snippet of rich_snippets) {
    const properties = snippet as any;
    if (snippet.__typename === 'ProductReviewSnippet') {
      schema.push({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: properties.name,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: properties.rating_value,
          reviewCount: properties.rating_count,
          bestRating: properties.best_rating,
          worstRating: properties.worst_rating,
          sameAs: properties.same_as,
        },
      });
    }
  }

  return schema;
};

export const getIndexPageMeta = ({
  pathname,
  siteMetadata,
  frontmatter,
}: {
  pathname: string;
  siteMetadata: Site['siteMetadata'];
  frontmatter: Frontmatter;
}) => {
  const { meta } = frontmatter;
  const { siteUrl, siteName } = siteMetadata;

  const canonicalUrl = getCanonicalUrl(siteUrl, pathname);
  const title = frontmatter.title;

  const description = trimText(meta?.description, MAX_META_DESCRIPTION_LENGTH);
  const image = `${siteUrl}${meta?.og_image}`;

  const socialz = [];
  socialz.push(
    { name: 'description', content: description },
    { name: 'author', content: BUNNY_AUTHOR },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:site_name', content: siteName },
    { property: 'og:image', content: image },
    { property: 'og:image:width', content: OG_IMAGE_WIDTH },
    { property: 'og:image:height', content: OG_IMAGE_HEIGHT },
    { property: 'og:type', content: 'website' },
    { property: 'article:tag', content: title },
    { property: 'article:publisher', content: BUNNY_FB },
    { name: 'twitter:url', content: canonicalUrl },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:site', content: BUNNY_TW },
    { name: 'twitter:card', content: 'summary' }
  );

  const robotsArr = [];

  if (meta?.no_index) {
    robotsArr.push('noindex');
  }
  if (meta?.no_follow) {
    robotsArr.push('nofollow');
  }

  const robotsMeta =
    meta?.no_index || meta?.no_follow
      ? { name: 'robots', content: robotsArr.join(', ') }
      : null;

  return {
    title,
    site_name: siteName,
    description,
    // TODO: very low-priority, add keywords
    image,
    canonical_url: canonicalUrl,
    socialz,
    robotsMeta: robotsMeta || undefined,
  };
};

export const getAcademyArticleMeta = ({
  pathname,
  siteMetadata,
  frontmatter,
  article,
}: {
  pathname: string;
  siteMetadata: any;
  frontmatter: any;
  article: AcademyArticle;
}) => {
  const { meta } = frontmatter;
  const { siteUrl, siteName } = siteMetadata;

  const canonicalUrl = getCanonicalUrl(siteUrl, pathname);
  const title = article.MetaTitle;
  const description = trimText(article.MetaDesc, MAX_META_DESCRIPTION_LENGTH);
  const keywords = article.MetaKeywords || undefined;
  const image = getAcademyMetaImageUrl(article, siteUrl, meta?.og_image);

  const socialz = [];
  socialz.push(
    { name: 'description', content: description },
    { name: 'author', content: BUNNY_AUTHOR },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:site_name', content: siteName },
    { property: 'og:image', content: image },
    { property: 'og:image:width', content: OG_IMAGE_WIDTH },
    { property: 'og:image:height', content: OG_IMAGE_HEIGHT },
    { property: 'og:type', content: 'article' },
    { property: 'article:tag', content: article.Category },
    { property: 'article:publisher', content: BUNNY_FB },
    { name: 'twitter:url', content: canonicalUrl },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:site', content: BUNNY_TW },
    { name: 'twitter:card', content: 'summary' }
  );

  if (keywords) {
    socialz.push({ name: 'keywords', content: keywords });
  }

  return {
    title,
    site_name: siteName,
    description,
    keywords,
    image,
    canonical_url: canonicalUrl,
    socialz,
  };
};

export const getCanonicalUrl = (siteUrl: string, pathname: string) => {
  return `${siteUrl}${pathname}${!pathname.endsWith('/') ? '/' : ''}`;
};

export const getBlogPostMeta = ({
  pathname,
  siteMetadata,
  frontmatter,
  post,
}: {
  pathname: string;
  siteMetadata: any;
  frontmatter: any;
  post: BlogPost;
}) => {
  const { meta } = frontmatter;
  const { siteName, siteUrl } = siteMetadata;
  const canonicalUrl = getCanonicalUrl(siteUrl, pathname);

  const title = post.meta_title;
  const description = trimText(
    post.meta_description,
    MAX_META_DESCRIPTION_LENGTH
  );

  const image = getBlogMetaImageUrl(post, siteUrl, meta?.og_image);
  const article_tags = post.tags.map(tag => ({
    property: 'article:tag',
    content: tag.name,
  }));
  const twitter_tags = post.tags.map(tag => tag.name).join(', ');

  const publishedAt = new Date(post.published_at).toISOString();
  const updatedAt = new Date(post.updated_at).toISOString();

  const socialz = [];
  socialz.push(
    { name: 'description', content: description },
    { name: 'author', content: BUNNY_AUTHOR },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:site_name', content: siteName },
    { property: 'og:image', content: image },
    { property: 'og:image:width', content: OG_IMAGE_WIDTH },
    { property: 'og:image:height', content: OG_IMAGE_HEIGHT },
    { property: 'og:type', content: 'article' },
    ...article_tags,
    { property: 'article:published_time', content: publishedAt },
    { property: 'article:modified_time', content: updatedAt },
    { property: 'article:publisher', content: BUNNY_FB },
    { name: 'twitter:url', content: canonicalUrl },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:site', content: BUNNY_TW },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:label1', content: 'Written by' },
    { name: 'twitter:data1', content: post.primary_author.name },
    { name: 'twitter:label2', content: 'Filed under' },
    { name: 'twitter:data2', content: twitter_tags }
  );

  return {
    title,
    site_name: siteName,
    description,
    image,
    canonical_url: canonicalUrl,
    socialz,
  };
};

export const fillVars = (value: any, templateVars: any) => {
  if (typeof value !== 'string') {
    if (typeof value === 'object' && value) {
      for (const [key, value_sub] of Object.entries(value)) {
        value[key] = fillVars(value_sub, templateVars);
      }
      return value;
    } else {
      return value;
    }
  }

  // eslint-disable-next-line no-useless-escape
  let varsArray = value.match(/[^{\}]+(?=})/g);
  let varsFilled = value;

  if (!varsArray || !varsArray.length) {
    return value;
  }

  varsArray.forEach(element => {
    varsFilled = varsFilled.replace(
      new RegExp('{' + element + '}', 'g'),
      templateVars[element]
    );
  });

  return varsFilled;
};

/**
 * Find all CDN ids in a block
 */
const findSectionGraphs = (blocks: Block[]) => {
  const foundBlocks = blocks.reduce(
    (acc, { _template, graph_items, time_scale, continents }) => {
      if (_template === 'block__graph') {
        acc.graph_items.push(...graph_items);
        acc.time_scale.push(time_scale);
        acc.continents.push(continents);
      }

      return acc;
    },
    {
      graph_items: [],
      time_scale: [],
      continents: [],
    } as CMSSectionGraphs
  );

  return foundBlocks;
};

/**
 * Find all CDN ids in a section
 */
export const findPageGraphs = (sections: Section[]) => {
  const foundSections = sections.reduce(
    (acc, section: Section) => {
      const blocksCDNIds = findSectionGraphs(section.blocks);
      return {
        graph_items: [...acc.graph_items, ...blocksCDNIds.graph_items],
        time_scale: [...acc.time_scale, ...blocksCDNIds.time_scale],
        continents: [...acc.continents, ...blocksCDNIds.continents],
      };
    },
    {
      graph_items: [],
      time_scale: [],
      continents: [],
    } as CMSSectionGraphs
  );

  return {
    graph_items: Array.from(new Set(foundSections.graph_items)),
    time_scale: Array.from(new Set(foundSections.time_scale)),
    continents: Array.from(new Set(foundSections.continents)),
  };
};

export function handleScroll(e: Event, href: string) {
  e.preventDefault();
  const section = document.querySelector(href);
  if (!section) return;
  const y = section.getBoundingClientRect().top + window.pageYOffset - 110;

  window.scrollTo({ top: y, behavior: 'smooth' });
  // section?.scrollIntoView({ behavior: 'smooth' });
  window.history.pushState('', '', href);
}

function setCookie(name: string, value: string, days = 30) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; domain=.bunny.net`;
}

function getCookie(name: string) {
  const cookies = document.cookie.split('; ');
  for (const cookie of cookies) {
    const [key, value] = cookie.split('=');
    if (key === name) return value;
  }
  return '';
}

export function trackPageVisit(
  cookieName: string,
  maxEntries = 5,
  daysToExpire = 30
) {
  const path = window.location.pathname;

  const segments = path.split('/').filter(Boolean); // Remove empty parts
  const pageName = segments.length ? segments[segments.length - 1] : null; // Get last segment

  if (!pageName) return; // Exit if URL has no valid segment

  // Get existing cookie value
  const existingPages = getCookie(cookieName);
  let pageArray = existingPages ? existingPages.split(',') : [];

  pageArray = pageArray.filter(page => page !== pageName);

  pageArray.unshift(pageName);

  // Keep only the last maxEntries
  if (pageArray.length > maxEntries) {
    pageArray = pageArray.slice(0, maxEntries);
  }

  // Store updated cookie
  setCookie(cookieName, pageArray.join(','), daysToExpire);
}

export function splitAfterLastDigit(input: string) {
  if (!input) {
    return [];
  }
  const lastDigitIndex = input.search(/\d(?!.*\d)/); // Find index of last digit

  if (lastDigitIndex === -1) {
    // No digit found
    return ['', convertWhitespaceToHTML(input)];
  }

  const before = input.slice(0, lastDigitIndex + 1);
  const afterRaw = input.slice(lastDigitIndex + 1);
  const afterHTML = convertWhitespaceToHTML(afterRaw);

  return [before, afterHTML];
}

function convertWhitespaceToHTML(str: string) {
  return str
    .replace(/\r\n|\r|\n/g, '<br>') // newlines to <br>
    .replace(/\t/g, '&emsp;') // tabs to em space
    .replace(/ /g, '&nbsp;'); // spaces to non-breaking space
}

export function convertJustification(input?: string) {
  if (!input) {
    return '';
  }
  switch (input) {
    case 'left':
      return 'flex-start';
    case 'right':
      return 'flex-end';
    default:
      return '';
  }
}

export function checkIfFromEurope() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Basic check
  if (tz.startsWith('Europe/')) return true;

  // Edge cases for European time zones that are not in the Europe/ region
  const europeanExtras = ['Asia/Istanbul', 'Asia/Nicosia', 'Asia/Tbilisi'];
  return europeanExtras.includes(tz);
}

export function fileToBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result); // data:image/png;base64,...
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
